import { useEffect, useRef, useState, useCallback, createContext, useContext } from 'react';
import { supabase } from '../supabaseClient';

// Screen labels — must match TITLES in Layout.js exactly
export const SCREEN_LABELS = {
  overview:'Overview', growth:'Growth', users:'Users',
  directory:'Directory', sana:'Sana', system:'System',
  locations:'Locations', passmanager:'Members', content:'Content',
  campaigns:'Campaigns', scheduler:'Scheduler', waitlist:'Waitlist',
  calendar:'Calendar', communications:'Chat', languages:'Languages', roles:'Roles & Access',
  userdetail:'User Lookup', travelprofiles:'Travel Profiles',
  nps:'NPS & Feedback', retention:'Retention', saved:'Saved Locations',
  journey:'User Journey', cohort:'Cohort Retention', peak:'Peak Usage',
  revenue:'Revenue & LTV',
  data:'Data Health', dataquality:'Data Quality', verifyqueue:'Verify Queue',
  locationeditor:'Location Editor', bulkeditor:'Bulk Editor',
  locationprofile:'Location Search', duplicates:'Data Cleanup',
  tagnormalizer:'Tag Normalizer', locationquality:'Location Quality',
  coverage:'Coverage', pipeline:'Pipeline', geocoding:'Geocoding',
  coverageplanner:'Coverage Planner', synctrigger:'Sync Trigger',
  pushnotifications:'Push Notifications', email:'Email', social:'Social',
  abtest:'A/B Tests', utm:'UTM & Referral', search:'Search',
  commshub:'Comms Hub', sanaeditor:'Sana Editor',
  notices:'Notices', featured:'App Content', betaaccess:'Beta Access',
  aiassistant:'AI Assistant', milestones:'Milestones',
  fraud:'Fraud & Abuse', launchreadiness:'Launch Tracker',
  perf:'Performance', apicostmonitor:'API Costs', teamproductivity:'Team Productivity',
  activitylog:'Activity Log', team:'Team Hub',
  notifications:'Notifications', telegram:'Telegram',
};

export default function usePresence(me, currentScreen) {
  const [onlineUsers, setOnlineUsers] = useState({}); // { initials: { name, initials, screen, joinedAt } }
  const channelRef = useRef(null);

  const updateScreen = useCallback((screen) => {
    if (!channelRef.current || !me) return;
    channelRef.current.track({
      initials:  me.initials,
      name:      me.name,
      email:     me.email,
      screen:    screen,
      joinedAt:  Date.now(),
    }).catch(() => {});
  }, [me]);

  useEffect(() => {
    if (!me) return;

    const ch = supabase.channel('admin-presence', {
      config: { presence: { key: me.initials } },
    });

    ch.on('presence', { event: 'sync' }, () => {
      const state = ch.presenceState();
      const users = {};
      Object.entries(state).forEach(([key, presences]) => {
        const p = presences[0];
        if (p) users[p.initials] = p; // include everyone including self
      });
      setOnlineUsers(users);
    });

    ch.on('presence', { event: 'join' }, ({ newPresences }) => {
      newPresences.forEach(p => {
        setOnlineUsers(prev => ({ ...prev, [p.initials]: p }));
      });
    });

    ch.on('presence', { event: 'leave' }, ({ leftPresences }) => {
      leftPresences.forEach(p => {
        setOnlineUsers(prev => {
          const next = { ...prev };
          delete next[p.initials];
          return next;
        });
      });
    });

    ch.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        await ch.track({
          initials:  me.initials,
          name:      me.name,
          email:     me.email,
          screen:    currentScreen,
          joinedAt:  Date.now(),
        });
      }
    });

    channelRef.current = ch;
    return () => {
      ch.untrack().then(() => supabase.removeChannel(ch));
    };
  }, [me]); // eslint-disable-line

  // Update screen when it changes
  useEffect(() => {
    if (!channelRef.current || !me) return;
    updateScreen(currentScreen);
  }, [currentScreen, updateScreen, me]);

  return { onlineUsers, updateScreen };
}

export const PresenceContext = createContext({});
export function usePresenceContext() { return useContext(PresenceContext); }
